import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { clinicInfo } from '../data/mockData';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(data.message || 'Message sent successfully! We\'ll get back to you soon.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        toast.error(data.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section style={{ 
        background: 'var(--bg-secondary)',
        padding: '80px 0',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container">
          <h1 className="hero-large" style={{ marginBottom: '16px' }}>
            Contact Us
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Get in touch with our team for any questions or appointments
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding">
        <div className="container">
          <div className="grid-two-column">
            {/* Contact Form */}
            <div>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                Send us a Message
              </h2>
              
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="name" className="body-regular" style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Suisse Medium, sans-serif'
                  }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      fontSize: '14px',
                      fontFamily: 'Suisse Regular, sans-serif',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="email" className="body-regular" style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Suisse Medium, sans-serif'
                  }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      fontSize: '14px',
                      fontFamily: 'Suisse Regular, sans-serif',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="phone" className="body-regular" style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Suisse Medium, sans-serif'
                  }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      fontSize: '14px',
                      fontFamily: 'Suisse Regular, sans-serif',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="subject" className="body-regular" style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Suisse Medium, sans-serif'
                  }}>
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      fontSize: '14px',
                      fontFamily: 'Suisse Regular, sans-serif',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <label htmlFor="message" className="body-regular" style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Suisse Medium, sans-serif'
                  }}>
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      fontSize: '14px',
                      fontFamily: 'Suisse Regular, sans-serif',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isSubmitting}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: isSubmitting ? 0.6 : 1
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  {!isSubmitting && <Send size={16} />}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                Contact Information
              </h2>
              
              <div style={{
                background: 'var(--bg-secondary)',
                padding: '32px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)',
                marginBottom: '32px'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <Phone size={20} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }} />
                    <div>
                      <div className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        Phone & WhatsApp
                      </div>
                      <a 
                        href={`tel:${clinicInfo.phone}`}
                        className="body-regular"
                        style={{ color: 'var(--text-primary)', textDecoration: 'none' }}
                      >
                        {clinicInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '16px' }}>
                    <Mail size={20} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }} />
                    <div>
                      <div className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        Email
                      </div>
                      <a 
                        href={`mailto:${clinicInfo.email}`}
                        className="body-regular"
                        style={{ color: 'var(--text-primary)', textDecoration: 'none' }}
                      >
                        {clinicInfo.email}
                      </a>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '16px' }}>
                    <MapPin size={20} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }} />
                    <div>
                      <div className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        Address
                      </div>
                      <div className="body-regular" style={{ color: 'var(--text-primary)' }}>
                        {clinicInfo.address}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '16px' }}>
                    <Clock size={20} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }} />
                    <div>
                      <div className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        Working Hours
                      </div>
                      <div className="body-regular" style={{ color: 'var(--text-primary)' }}>
                        {clinicInfo.workingHours}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map Embed */}
              <div style={{
                background: 'var(--bg-secondary)',
                padding: '0',
                borderRadius: '0px',
                border: '1px solid var(--border-light)',
                overflow: 'hidden',
                height: '350px'
              }}>
                <iframe 
                  src={clinicInfo.mapEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="The Eternal Care Location"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
